/**
 * Background blur / replacement for the webcam photo. A person-segmentation model (MediaPipe Selfie
 * Segmentation, served from /mediapipe by this site) runs in the browser on the picture that was just taken,
 * so the picture is never sent anywhere before the player accepts it. The model (about 12 MB with the
 * runtime, loaded once and cached by the browser) is only fetched when the player picks Blur or Colour.
 */

type Mask = HTMLCanvasElement | HTMLImageElement | ImageBitmap;
type SegmenterResults = { segmentationMask: Mask };
type Segmenter = {
  setOptions(options: { modelSelection?: number }): void;
  onResults(listener: (results: SegmenterResults) => void): void;
  initialize(): Promise<void>;
  send(inputs: { image: HTMLCanvasElement }): Promise<void>;
};
type SegmenterConstructor = new (config: { locateFile: (file: string) => string }) => Segmenter;

/** Where the model files are (next to the page, whatever the layout address is). */
function modelUrl(file: string): string {
  return new URL("mediapipe/" + file, document.baseURI).href;
}

let segmenter: Promise<Segmenter> | undefined;

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-segmenter="1"]');
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("The model could not be loaded.")));
      if ((window as unknown as { SelfieSegmentation?: unknown }).SelfieSegmentation) resolve();
      return;
    }
    const tag = document.createElement("script");
    tag.src = src;
    tag.async = true;
    tag.dataset.segmenter = "1";
    tag.onload = () => resolve();
    tag.onerror = () => reject(new Error("The model could not be loaded."));
    document.head.appendChild(tag);
  });
}

/** Loads the model the first time; later calls reuse it. A failed load can be tried again. */
function loadSegmenter(): Promise<Segmenter> {
  if (!segmenter) {
    segmenter = (async () => {
      await loadScript(modelUrl("selfie_segmentation.js"));
      const Ctor = (window as unknown as { SelfieSegmentation?: SegmenterConstructor }).SelfieSegmentation;
      if (!Ctor) {
        throw new Error("The model could not be started.");
      }
      const s = new Ctor({ locateFile: modelUrl });
      s.setOptions({ modelSelection: 0 }); // the general model: better for a face close to the camera
      await s.initialize();
      return s;
    })();
    segmenter.catch(() => {
      segmenter = undefined;
    });
  }
  return segmenter;
}

/** A canvas with the same size as {@code image} whose opaque part is the person. */
export async function personMask(image: HTMLCanvasElement): Promise<HTMLCanvasElement> {
  const s = await loadSegmenter();
  return new Promise<HTMLCanvasElement>((resolve, reject) => {
    s.onResults((results) => {
      const mask = document.createElement("canvas");
      mask.width = image.width;
      mask.height = image.height;
      const ctx = mask.getContext("2d");
      if (!ctx) {
        reject(new Error("The browser cannot draw the picture."));
        return;
      }
      ctx.drawImage(results.segmentationMask as CanvasImageSource, 0, 0, mask.width, mask.height);
      resolve(mask);
    });
    s.send({ image }).catch(reject);
  });
}

export type Background = { kind: "original" } | { kind: "blur" } | { kind: "colour"; colour: string };

/** A strongly blurred copy of the picture (down and up again, which every browser can do). */
function blurred(source: HTMLCanvasElement): HTMLCanvasElement {
  let current = source;
  for (const divisor of [10, 5]) {
    const small = document.createElement("canvas");
    small.width = Math.max(8, Math.round(source.width / divisor));
    small.height = Math.max(8, Math.round(source.height / divisor));
    const sctx = small.getContext("2d");
    if (sctx) {
      sctx.imageSmoothingEnabled = true;
      sctx.drawImage(current, 0, 0, small.width, small.height);
    }
    current = small;
  }
  const out = document.createElement("canvas");
  out.width = source.width;
  out.height = source.height;
  const octx = out.getContext("2d");
  if (octx) {
    octx.imageSmoothingEnabled = true;
    octx.imageSmoothingQuality = "high";
    octx.drawImage(current, 0, 0, out.width, out.height);
  }
  return out;
}

/**
 * The picture with its background blurred or replaced by a colour.
 * @param mask the result of {@link personMask} for the same picture.
 */
export function withBackground(source: HTMLCanvasElement, mask: HTMLCanvasElement, background: Background): HTMLCanvasElement {
  if (background.kind === "original") {
    return source;
  }
  const out = document.createElement("canvas");
  out.width = source.width;
  out.height = source.height;
  const ctx = out.getContext("2d");
  if (!ctx) {
    return source;
  }
  // The background layer.
  if (background.kind === "blur") {
    ctx.drawImage(blurred(source), 0, 0);
  } else {
    ctx.fillStyle = background.colour;
    ctx.fillRect(0, 0, out.width, out.height);
  }
  // The person: the picture where the mask is opaque.
  const person = document.createElement("canvas");
  person.width = source.width;
  person.height = source.height;
  const pctx = person.getContext("2d");
  if (!pctx) {
    return source;
  }
  pctx.drawImage(mask, 0, 0);
  pctx.globalCompositeOperation = "source-in";
  pctx.drawImage(source, 0, 0);
  ctx.drawImage(person, 0, 0);
  return out;
}
